/**
 * <amq-gallery-preload>: loads the large product-gallery images once the page is idle
 * (snippets/amq_gallery_preload.liquid).
 *
 * The slideshow renders every slide but the first with loading="lazy". After the load event, when
 * the browser is idle, this element switches the remaining lazy slide images in the surrounding
 * media column to eager. A pointerdown in the media column or a pointer over the thumbnail strip or
 * the arrows starts it straight away. With Save-Data only the pointer does.
 *
 * Switching variants or combined-listing products morphs the media column with server HTML, which
 * brings back lazy slide images. Once preloading has started, a MutationObserver switches those too.
 * Hidden copies (the slideshow next to the desktop grid) have no layout box and stay lazy.
 */
class AmqGalleryPreload extends HTMLElement {
  /** @type {Element | null} */
  #scope = null;

  /** @type {MutationObserver | undefined} */
  #observer;

  /** @type {(() => void) | undefined} */
  #cancelIdle;

  #started = false;

  #frame = 0;

  connectedCallback() {
    this.#scope = this.closest('.product-information__media') ?? this.parentElement;
    if (!this.#scope) return;

    if (this.#started) {
      this.#preload();
      this.#observe();
      return;
    }

    this.#scope.addEventListener('pointerdown', this.#start);
    this.#scope.addEventListener('pointerover', this.#onPointerOver);

    if (/** @type {any} */ (navigator).connection?.saveData) return;
    if (document.readyState === 'complete') this.#scheduleIdle();
    else window.addEventListener('load', this.#scheduleIdle, { once: true });
  }

  disconnectedCallback() {
    this.#removeTriggers();
    this.#observer?.disconnect();
    this.#observer = undefined;
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  #scheduleIdle = () => {
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(this.#start, { timeout: 3000 });
      this.#cancelIdle = () => window.cancelIdleCallback(id);
    } else {
      const id = window.setTimeout(this.#start, 0);
      this.#cancelIdle = () => window.clearTimeout(id);
    }
  };

  /** @param {Event} event */
  #onPointerOver = (event) => {
    if (event.target instanceof Element && event.target.closest('slideshow-controls, slideshow-arrows')) {
      this.#start();
    }
  };

  #start = () => {
    if (this.#started || !this.#scope) return;
    this.#started = true;
    this.#removeTriggers();
    this.#preload();
    this.#observe();
  };

  #observe() {
    if (!this.#scope) return;

    this.#observer?.disconnect();
    this.#observer = new MutationObserver(() => {
      if (this.#frame) return;
      this.#frame = requestAnimationFrame(() => {
        this.#frame = 0;
        this.#preload();
      });
    });
    this.#observer.observe(this.#scope, { childList: true, subtree: true, attributes: true, attributeFilter: ['loading'] });
  }

  #removeTriggers() {
    this.#scope?.removeEventListener('pointerdown', this.#start);
    this.#scope?.removeEventListener('pointerover', this.#onPointerOver);
    window.removeEventListener('load', this.#scheduleIdle);
    this.#cancelIdle?.();
    this.#cancelIdle = undefined;
  }

  #preload() {
    if (!this.#scope) return;

    for (const image of this.#scope.querySelectorAll('media-gallery slideshow-slide img[loading="lazy"]')) {
      if (!(image instanceof HTMLImageElement) || image.getClientRects().length === 0) continue;
      image.loading = 'eager';
    }
  }
}

if (!customElements.get('amq-gallery-preload')) {
  customElements.define('amq-gallery-preload', AmqGalleryPreload);
}
